import type { OpenerGroup } from '../../api/useGroups';

/**
 * Group filter for the opener page. The selection persists in the URL as
 * `?group=<id>` (sentinel `ungrouped` for the no-group bucket; absent =
 * All groups) so it survives reloads and is shareable. Pure helpers —
 * EntryPage owns the `useSearchParams` wiring.
 */

export const GROUP_PARAM = 'group';
export const UNGROUPED_SENTINEL = 'ungrouped';

export type GroupSelection =
  | { kind: 'all' }
  | { kind: 'ungrouped' }
  | { kind: 'group'; groupId: string };

export const ALL_GROUPS: GroupSelection = { kind: 'all' };

export function readGroupSelection(params: URLSearchParams): GroupSelection {
  const raw = params.get(GROUP_PARAM);
  if (!raw) return ALL_GROUPS;
  if (raw === UNGROUPED_SENTINEL) return { kind: 'ungrouped' };
  return { kind: 'group', groupId: raw };
}

/** Returns a NEW URLSearchParams with the selection applied; every other
 *  param is preserved untouched. "All" deletes the param entirely. */
export function writeGroupSelection(
  params: URLSearchParams,
  selection: GroupSelection
): URLSearchParams {
  const next = new URLSearchParams(params);
  if (selection.kind === 'all') next.delete(GROUP_PARAM);
  else if (selection.kind === 'ungrouped')
    next.set(GROUP_PARAM, UNGROUPED_SENTINEL);
  else next.set(GROUP_PARAM, selection.groupId);
  return next;
}

/**
 * Dead-group fallback: a URL pointing at a group that no longer exists
 * (deleted in another tab) resolves to All — but ONLY once the groups
 * query has successfully resolved. While it is still loading the
 * selection is kept as-is so the page never flash-falls-back to the full
 * list and then snaps to the group.
 */
export function resolveGroupSelection(
  selection: GroupSelection,
  groups: Pick<OpenerGroup, '_id'>[] | undefined,
  groupsResolved: boolean
): GroupSelection {
  if (selection.kind !== 'group') return selection;
  if (!groupsResolved) return selection;
  const exists = (groups ?? []).some(g => g._id === selection.groupId);
  return exists ? selection : ALL_GROUPS;
}

export function pairMatchesGroup(
  pair: { openerGroupId?: string | null },
  selection: GroupSelection
): boolean {
  if (selection.kind === 'all') return true;
  if (selection.kind === 'ungrouped') return pair.openerGroupId == null;
  return pair.openerGroupId === selection.groupId;
}

/**
 * Stable identity key for a selection — same selection always yields the
 * same key, distinct selections never collide. Drives effects that must
 * reset exactly when the operator switches the group filter (e.g. clearing
 * the bulk-select checkbox set), regardless of object-reference churn.
 */
export function groupSelectionKey(selection: GroupSelection): string {
  return selection.kind === 'group'
    ? `group:${selection.groupId}`
    : selection.kind;
}
